/**
 * Copyright 2025 AutoZone, Inc.
 * Content is confidential to and proprietary information of AutoZone, Inc., its
 * subsidiaries and affiliates.
 */
'use memo';
import NextImage from '@/components/NextImage';
import { TableCell } from '@/components/TableCell/TableCell';
import { Text } from '@az/starc-ui';
import azCommonStyles from '@/theme/globals.module.scss';
import { SmartLink as Link } from '@/utils/smartLink';
import cx from 'classnames';
import styles from './style.module.scss';
import { useLabels } from '@/hooks/useLabels';
import { FlexGrid } from '@/components/FlexGrid/FlexGrid';

export type ComparisonTableHeaderCellProps = {
  imageURL: string | null;
  productURL: string | null;
  productName: string | null;
  isCurrentSKU?: boolean;
  onPDPComparisonLinkClick: () => void;
};

const labelMap = {
  currentlyViewing: 'label_PDPComparison_currentlyViewing',
};

export function ComparisonTableHeaderCell({
  imageURL,
  productURL,
  productName,
  isCurrentSKU = false,
  onPDPComparisonLinkClick,
}: ComparisonTableHeaderCellProps) {
  const labels = useLabels(labelMap);

  const cellContents = (
    <div className={styles.comparisonProductInformation}>
      {imageURL && (
        <NextImage src={imageURL} width={180} height={180} className={styles.tableImage} />
      )}
      <span className={cx(azCommonStyles['az-body-1-medium'], styles.productName)}>
        {productName}
      </span>
    </div>
  );

  return (
    <TableCell component="th" className={styles.headerCell}>
      <FlexGrid gap={4} flexDirection="column">
        <div
          className={cx(styles.currentlyViewingBox, {
            [styles.grayBackground]: isCurrentSKU,
          })}
        >
          {isCurrentSKU ? (
            <Text className={styles.currentlyViewingText} color="on-primary">
              {labels.currentlyViewing}
            </Text>
          ) : (
            <div className={styles.currentlyViewingPlaceholder} />
          )}
        </div>

        <div className={styles.productInformationWrapper}>
          {productURL ? (
            <Link
              to={productURL || ''}
              className={azCommonStyles['az-color-inherit']}
              onClick={onPDPComparisonLinkClick}
            >
              {cellContents}
            </Link>
          ) : (
            cellContents
          )}
        </div>
      </FlexGrid>
    </TableCell>
  );
}
